import { LanguagesEnum } from '@/api/enums';
import { TopbarMenu } from '@/components/navigation';
import { SurveyPreview } from '@/components/security-culture';
import { useSurveyForUser } from '@/hooks/query/quizzes.hooks';
import { FlexContainer } from '@/ui/styled-ui';
import { isMobile } from '@/utils/helpers/ui.helper';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LoadingPage } from '../LoadingPage';

const Container = styled.div`
  overflow: hidden;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 0 repeat(1, 1fr);
  grid-column-gap: 0;
  grid-row-gap: 0;
  min-height: 800px;
  height: 100%;
  background-color: var(--purple-dark);

  > ::-webkit-scrollbar-track {
    margin: 16px 0 0 0;
  }
  .content {
    grid-area: 1 / 2 / 6 / 6;
    background-color: var(--white-main);
  }

  &.with-topbar {
    min-height: auto;
    grid-template-rows: var(--top-nav-height) repeat(2, 1fr);

    .content {
      grid-area: 2 / 2 / 6 / 6;
      border-radius: var(--default-border-radius) var(--default-border-radius) 0
        0;
      width: calc(100% - var(--default-padding));
    }
  }

  .content.without-sidebar {
    grid-area: 2 / 1 / 6 / 6 !important;
    max-width: var(--max-dashboard-width);
    margin: 0 auto;
    width: calc(100% - calc(var(--big-padding) * 2));

    @media only screen and (max-device-width: 812px) {
      width: calc(100% - calc(var(--medium-padding) * 2)) !important;
    }

    &.mobile {
      width: 100% !important;
      border-radius: 0;
      padding: var(--medium-padding);
    }
  }
`;

const TopMenu = styled.div`
  width: 100%;
  grid-area: 1 / 1 / 1 / 6;
  background: var(--purple-dark);
`;

const Content = styled.div`
  position: relative;
  overflow: auto;
  width: 100%;
  padding: var(--default-padding);
`;

const StyledContainer = styled(FlexContainer)`
  height: 100%;
  width: 100%;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--big-padding);
`;

export const TakeSurveyPage: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const token = params?.token;
  const sentSurveyId = params?.surveyId;

  const urlLanguage = new URLSearchParams(location.search).get('language');

  const { survey, isLoading, error } = useSurveyForUser({
    sentSurveyId,
    token,
    enabled: !!sentSurveyId,
  });

  useEffect(() => {
    if (survey && survey.logo) {
      sessionStorage.setItem('logo', survey.logo);
    }
  }, [survey]);

  const checkIfSurveyIsSubmitted = (error: any) =>
    error?.message ==
    'You have already submitted your answers for this survey.';

  return (
    <Container className="with-topbar notranslate" translate="no">
      <TopMenu>
        <TopbarMenu fallbackLogo={survey?.logo} />
      </TopMenu>
      <Content
        className={['content', 'without-sidebar', isMobile() ? 'mobile' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <StyledContainer>
          {isLoading && (
            <LoadingPage message={t('security.culture.survey.loading')} />
          )}

          {!isLoading && (survey || checkIfSurveyIsSubmitted(error)) && (
            <SurveyPreview
              sentSurveyId={sentSurveyId}
              survey={survey}
              token={token}
              isSubmitted={checkIfSurveyIsSubmitted(error)}
              urlLanguage={
                urlLanguage ? (urlLanguage as LanguagesEnum) : undefined
              }
            />
          )}

          {!isLoading && !survey && !checkIfSurveyIsSubmitted(error) && (
            <FlexContainer
              direction="column"
              align="center"
              justify="center"
              height="100%"
            >
              <h3>{t('sc.survey.not.found')}</h3>
              <p>{t('sc.survey.not.found.description')}</p>
            </FlexContainer>
          )}
        </StyledContainer>
      </Content>
    </Container>
  );
};
